import axios from "axios";
import { BACKEND_URL } from "../config_and_helpers/config";
import { showToast } from "../config_and_helpers/helpers";
import { logError, logInfo } from "../config_and_helpers/logger";

/**
 * Validates the authentication token of a user.
 *
 * This function sends a GET request to the backend API to validate the user's authentication token.
 * If the request is successful, it indicates that the token is valid, and the function returns an object
 * with a 'success' key set to true. If the token is not valid or the request fails, it returns an
 * object with the 'success' key set to false.
 *
 * The response `user_details` is returned alongside so session restore can pick up
 * server-side role changes (`account_role` / `is_lab_admin` / `is_lab_viewer`) without
 * requiring the user to log in again.
 *
 * @async
 * @param {string} token - The authentication token of the user to validate.
 * @returns {Promise<{success:boolean, userDetails?:Object|null}>} - Validation status plus fresh user_details.
 */
export const validateToken = async (token) => {
  // Try to send a GET request to validate the token
  try {
    const response = await axios.get(`${BACKEND_URL}users/validate_token/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // If the request is successful, return an object with success set to true
    return { success: true, userDetails: response?.data?.user_details ?? null };

    // If any error occurs during the token validation
  } catch (error) {
    // Return an object with success set to false
    return { success: false };
  }
};

/**
 * Registers a new user.
 *
 * This function sends a POST request to the backend API with the user details to register a new user.
 * After the user is successfully registered, it attempts to log in the new user.
 * If the user registration and login are both successful, it returns an object with the 'success' key set to true.
 * If the registration or the login fails, it returns an error message.
 *
 * @async
 * @param {Object} userData - The details of the new user to register.
 * @param {function} setToken - The function to set the user's authentication token.
 * @param {function} setUserDetails - The function to set the details of the user.
 * @param {function} setNotifications - The function to set the user's notifications.
 * @returns {Promise<Object>} - An object representing registration and login success status.
 * @throws Will throw an error if the registration request fails.
 */
export const signup = async (
  userData,
  setToken,
  setUserDetails,
  setNotifications,
) => {
  // Try to send a POST request to register a new user
  try {
    const response = await axios.post(`${BACKEND_URL}users/`, userData, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    let result = { success: true };

    // After the user is successfully registered, attempt to log in the new user
    if (response) {
      const loginResponse = await login({
        credentials: {
          username: userData.username,
          password: userData.password,
        },
        setToken,
        setUserDetails,
        setNotifications,
      });

      // If the login fails after successfully registering
      if (loginResponse && !loginResponse.success) {
        return { success: false, message: "Login failed after signup" };
      }

      // If registration and login are both successful
      return { success: true };
    }

    // Return the result
    return result;

    // If any error occurs during the registration or login
  } catch (error) {
    logError('user_client', 'signup failed', {
      error: error.response?.data || error.message,
    });
    // return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Logs in a user.
 *
 * This function sends a POST request to the backend API with the user's credentials to log in a user.
 * It also sets or updates the user's token, details, supplier status, and notifications in the respective hooks functions and local storage or session storage,
 * depending on whether the user chooses to remember the login information.
 * If the login is successful, it returns an object with the 'success' key set to true. If the login fails, it returns an error message.
 *
 * @async
 * @param {Object} parameters - The object that contains user's credentials, hooks functions and remember me status.
 * @param {Object} parameters.credentials - The username and password of the user.
 * @param {function} parameters.setToken - The function to set the user's authentication token.
 * @param {function} parameters.setUserDetails - The function to set the details of the user.
 * @param {function} parameters.setIsSupplier - The function to set the user's supplier status.
 * @param {function} parameters.setNotifications - The function to set the user's notifications.
 * @param {boolean} parameters.rememberMe - Whether to remember the user's login information or not.
 * @returns {Promise<Object>} - An object representing the login success status.
 * @throws Will throw an error if the login request fails.
 */
export const login = async ({
  credentials,
  setToken,
  setUserDetails,
  setIsSupplier = () => {},
  setNotifications = () => {},
  rememberMe = false,
}) => {
  // Prepare user credentials
  const userData = {
    username: credentials.username,
    password: credentials.password,
  };

  // Try to send a POST request to log in the user
  try {
    const res = await axios.post(`${BACKEND_URL}api-token-auth/`, userData, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    // If login is successful
    if (res.status === 200 && res.data) {
      // Update the user's token, details, and notifications
      setToken(res.data.token);
      setUserDetails(res.data.user_details);
      setNotifications(res.data.notifications);

      // If the user is a supplier, set the supplier status
      if (res.data.user_details.is_supplier) {
        setIsSupplier(true);
      }

      // Choose the appropriate web storage depending on rememberMe
      let storage = rememberMe ? localStorage : sessionStorage;

      // Store the user's token, details, supplier status, and notifications in web storage
      storage.setItem("token", res.data.token);
      storage.setItem("userDetails", JSON.stringify(res.data.user_details));
      storage.setItem("notifications", JSON.stringify(res.data.notifications));
      storage.setItem("isSupplier", String(res.data.user_details.is_supplier));
      storage.setItem("rememberMe", String(rememberMe));
    }

    // Return an object indicating success
    return { success: true };

    // If any error occurs during the login
  } catch (error) {
    logError('user_client', 'login failed', {
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Logs out a user.
 *
 * This function sends a POST request to the backend API to log out a user and then clears the local and session storages.
 * If the logout is successful, it returns an object with the 'success' key set to true. If the logout fails, it returns an error message.
 *
 * @async
 * @param {string} token - The authentication token of the user to logout.
 * @returns {Promise<Object>} - An object representing the logout success status.
 * @throws Will throw an error if the logout request fails.
 */
export const logout = async (token) => {
  // Try to send a POST request to log out the user
  try {
    await axios.post(
      `${BACKEND_URL}logout/`,
      {},
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      },
    );
    // Clear the local and session storages after logout
    localStorage.clear();
    sessionStorage.clear();

    // Return an object indicating success
    return { success: true };

    // If any error occurs during the logout
  } catch (error) {
    logError('user_client', 'logout failed', {
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Requests a password reset token for a user.
 *
 * This function sends a POST request to the backend API with the user's email to request a password reset token.
 * If the request is successful, it returns an object with the 'success' key set to true. If the request fails, it returns an error message.
 *
 * @async
 * @param {string} email - The email of the user to request a password reset token.
 * @returns {Promise<Object>} - An object representing the password reset token request success status.
 * @throws Will throw an error if the password reset token request fails.
 */
export const getPasswordToken = async (email) => {
  // Try to send a POST request to request a password reset token
  try {
    await axios.post(`${BACKEND_URL}api/password_reset/`, { email });

    // If the request is successful
    return { success: true };

    // If any error occurs during the request
  } catch (error) {
    logError('user_client', 'getPasswordToken failed', {
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Resets a user's password.
 *
 * This function sends a POST request to the backend API with the password reset token and the new password to reset a user's password.
 * If the password reset request is successful, it returns an object with the 'success' key set to true. If the request fails, it returns an error message.
 *
 * @async
 * @param {string} token - The password reset token of the user to reset the password.
 * @param {string} password - The new password of the user.
 * @returns {Promise<Object>} - An object representing the password reset success status.
 * @throws Will throw an error if the password reset request fails.
 */
export const resetPassword = async (token, password) => {
  // Try to send a POST request to reset the password
  try {
    await axios.post(`${BACKEND_URL}api/password_reset/confirm/`, {
      token,
      password,
    });

    // If the password reset request is successful
    return { success: true };

    // If any error occurs during the password reset request
  } catch (error) {
    logError('user_client', 'resetPassword failed', {
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Updates a user's profile via PATCH /users/{id}/.
 *
 * Session Context (`setUserDetails`) is updated only when editing the logged-in
 * user. Admin edits of another lab user return `user` without overwriting
 * the session identity.
 *
 * @async
 * @param {string} token - Auth token.
 * @param {string|number} userId - User pk to PATCH.
 * @param {Object} updatedData - Patch body (email, userprofile, optional account_role).
 * @param {function} [setUserDetails] - Session setter; only applied for self-edits.
 * @param {Object} [options]
 * @param {string|number} [options.sessionUserId] - Logged-in user id. When set and
 *   different from `userId`, session Context is not updated. When omitted, session
 *   is updated (legacy self-edit callers).
 * @returns {Promise<Object>} `{ success, user, toast }` or `{ success:false, status, messages, error }`
 */
export const updateUserProfile = async (
  token,
  userId,
  updatedData,
  setUserDetails,
  options = {},
) => {
  try {
    const response = await axios.patch(
      `${BACKEND_URL}users/${userId}/`,
      updatedData,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      },
    );

    const sessionUserId = options?.sessionUserId;
    const isSelfEdit =
      sessionUserId == null ||
      String(sessionUserId) === String(userId);

    if (isSelfEdit && typeof setUserDetails === 'function') {
      setUserDetails(response.data);
    }

    logInfo('user_client', 'updateUserProfile success', {
      userId,
      isSelfEdit,
      accountRole: response.data?.account_role ?? null,
    });

    return {
      success: true,
      user: response.data,
      toast: () =>
        showToast(
          'Account details edited successfully!',
          'success',
          'top-right',
          3000,
        ),
    };
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    logError('user_client', 'updateUserProfile failed', {
      userId,
      status: status ?? null,
      error: data || error.message,
    });

    let messages = [];
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      messages = Object.values(data).flat();
    } else if (Array.isArray(data)) {
      messages = data;
    } else if (typeof data === 'string') {
      messages = [data];
    } else if (error.message) {
      messages = [error.message];
    }

    if (status === 403) {
      messages = messages.length ? messages : ['Not allowed to edit this user.'];
    } else if (status === 404) {
      messages = messages.length ? messages : ['User not found.'];
    }

    return {
      success: false,
      status: status ?? null,
      messages,
      error: data || error.message,
    };
  }
};

/**
 * Checks if a username is unique.
 *
 * This function sends a GET request to the backend API to query if a username is unique.
 * If the request is successful, it returns a boolean indicating whether the username is unique or not.
 * If the request fails, it returns an error message.
 *
 * @async
 * @param {string} value - The username to check.
 * @returns {Promise<boolean | Object>} - True if the username is unique, false if not, or an object representing the error message if the request fails.
 * @throws Will throw an error if the request fails.
 */
export const checkUsername = async (value) => {
  // Try to send a GET request to check if the username is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}users/check_username/?value=${value}`,
    );

    // Return whether the username is unique or not
    return response.data.unique;

    // If any error occurs during the request
  } catch (error) {
    logError('user_client', 'checkUsername failed', {
      value,
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Checks if a username is unique (requires authentication).
 *
 * This function sends a GET request to the backend API to query if a username is unique. This operation requires authentication.
 * If the request is successful, it returns a boolean indicating whether the username is unique or not.
 * If the request fails, it returns an error message.
 *
 * @async
 * @param {string} token - The authentication token of the user.
 * @param {string} value - The username to check.
 * @returns {Promise<boolean | Object>} - True if the username is unique, false if not, or an object representing the error message if the request fails.
 * @throws Will throw an error if the request fails.
 */
export const checkUsernameAuthRequired = async (token, value) => {
  // Try to send a GET request to check if the username is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}users/check_username_auth_required/?value=${value}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      },
    );

    // Return whether the username is unique or not
    return response.data.unique;

    // If any error occurs during the request
  } catch (error) {
    logError('user_client', 'checkUsernameAuthRequired failed', {
      value,
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Checks if an email is unique.
 *
 * This function sends a GET request to the backend API to query if an email is unique.
 * If the request is successful, it returns a boolean indicating whether the email is unique or not.
 * If the request fails, it returns an error message.
 *
 * @async
 * @param {string} value - The email to check.
 * @returns {Promise<boolean | Object>} - True if the email is unique, false if not, or an object representing the error message if the request fails.
 * @throws Will throw an error if the request fails.
 */
export const checkEmail = async (value) => {
  // Try to send a GET request to check if the email is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}users/check_email/?value=${value}`,
    );

    // Return whether the email is unique or not
    return response.data.unique;

    // If any error occurs during the request
  } catch (error) {
    logError('user_client', 'checkEmail failed', {
      value,
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Checks if an email is unique (requires authentication).
 *
 * This function sends a GET request to the backend API to query if an email is unique. This operation requires authentication.
 * If the request is successful, it returns a boolean indicating whether the email is unique or not.
 * If the request fails, it returns an error message.
 *
 * @async
 * @param {string} token - The authentication token of the user.
 * @param {string} value - The email to check.
 * @returns {Promise<boolean | Object>} - True if the email is unique, false if not, or an object representing the error message if the request fails.
 * @throws Will throw an error if the request fails.
 */
export const checkEmailAuthRequired = async (token, value) => {
  // Try to send a GET request to check if the email is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}users/check_email_auth_required/?value=${value}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      },
    );

    // Return whether the email is unique or not
    return response.data.unique;

    // If any error occurs during the request
  } catch (error) {
    logError('user_client', 'checkEmailAuthRequired failed', {
      value,
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Checks if a phone number is unique (requires authentication).
 *
 * This function sends a GET request to the backend API to query if a phone number is unique. This operation requires authentication.
 * The phone number is divided into two parts: the prefix and the suffix.
 * If the request is successful, it returns a boolean indicating whether the phone number is unique or not.
 * If the request fails, it returns an error message.
 *
 * @async
 * @param {string} token - The authentication token of the user.
 * @param {string} prefix - The prefix part of the phone number to check.
 * @param {string} suffix - The suffix part of the phone number to check.
 * @returns {Promise<boolean | Object>} - True if the phone number is unique, false if not, or an object representing the error message if the request fails.
 * @throws Will throw an error if the request fails.
 */
export const checkPhoneAuthRequired = async (token, prefix, suffix) => {
  // Try to send a GET request to check if the phone number is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}users/check_phone_auth_required/?prefix=${prefix}&suffix=${suffix}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      },
    );

    // Return whether the phone number is unique or not
    return response.data.unique;

    // If any error occurs during the request
  } catch (error) {
    logError('user_client', 'checkPhoneAuthRequired failed', {
      prefix,
      suffix,
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Checks if a phone number is unique.
 *
 * This function sends a GET request to the backend API to query if a phone number is unique.
 * The phone number is divided into two parts: the prefix and the suffix.
 * If the request is successful, it returns a boolean indicating whether the phone number is unique or not.
 * If the request fails, it returns an error message.
 *
 * @async
 * @param {string} prefix - The prefix part of the phone number to check.
 * @param {string} suffix - The suffix part of the phone number to check.
 * @returns {Promise<boolean | Object>} - True if the phone number is unique, false if not, or an object representing the error message if the request fails.
 * @throws Will throw an error if the request fails.
 */
export const checkPhoneNumber = async (prefix, suffix) => {
  // Try to send a GET request to check if the phone number is unique
  try {
    const response = await axios.get(
      `${BACKEND_URL}users/check_phone/?prefix=${prefix}&suffix=${suffix}`,
    );

    // Return whether the phone number is unique or not
    return response.data.unique;

    // If any error occurs during the request
  } catch (error) {
    logError('user_client', 'checkPhoneNumber failed', {
      prefix,
      suffix,
      error: error.response?.data || error.message,
    });
    // Return the error message
    return Object.values(error.response.data);
  }
};

/**
 * Resolves a stable numeric user id from list payloads.
 *
 * @param {Object} user
 * @returns {number|null}
 */
const resolveUserId = user => {
  const rawId = user?.id ?? user?.user_id ?? user?.pk ?? null;
  const parsed = Number(rawId);
  return Number.isInteger(parsed) ? parsed : null;
};

/**
 * Creates a lab user via authenticated POST /users/ (Admin only).
 * Does not log in as the new user or mutate the caller session.
 *
 * @async
 * @param {string} token - Admin auth token.
 * @param {Object} userData - Create body (username, password, email, names, userprofile, optional account_role).
 * @returns {Promise<Object>} `{ success, user, toast }` or `{ success:false, status, messages, error }`
 */
export const createLabUser = async (token, userData) => {
  try {
    const response = await axios.post(`${BACKEND_URL}users/`, userData, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const userId = resolveUserId(response.data);
    const user = {
      ...response.data,
      user_id: userId,
      id: userId,
      account_role: response.data?.account_role || 'staff',
    };

    logInfo('user_client', 'createLabUser success', {
      userId,
      accountRole: user.account_role,
    });

    return {
      success: true,
      user,
      toast: () =>
        showToast(
          'Lab user created successfully.',
          'success',
          'top-right',
          3000,
        ),
    };
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    logError('user_client', 'createLabUser failed', {
      status: status ?? null,
      error: data || error.message,
    });

    let messages = [];
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      messages = Object.values(data).flat();
    } else if (Array.isArray(data)) {
      messages = data;
    } else if (typeof data === 'string') {
      messages = [data];
    } else if (error.message) {
      messages = [error.message];
    }

    if (status === 403) {
      messages = messages.length
        ? messages
        : ['Not allowed to create users.'];
    }

    return {
      success: false,
      status: status ?? null,
      messages,
      error: data || error.message,
    };
  }
};

/**
 * Lists lab users for the Admin account directory (GET /users/).
 * Staff callers receive only themselves from the backend.
 *
 * @async
 * @param {string} token
 * @returns {Promise<{success:boolean, users?:Object[], error?:*, status?:number|null}>}
 */
export const getLabUsers = async token => {
  try {
    const response = await axios.get(`${BACKEND_URL}users/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });
    const rawUsers = response.data?.results ?? response.data;
    const users = (Array.isArray(rawUsers) ? rawUsers : []).map(user => {
      const userId = resolveUserId(user);
      return {
        ...user,
        user_id: userId,
        id: userId,
        account_role: user?.account_role || 'staff',
      };
    });
    logInfo('user_client', 'getLabUsers success', { count: users.length });
    return { success: true, users };
  } catch (error) {
    const status = error.response?.status ?? null;
    logError('user_client', 'getLabUsers failed', {
      status,
      error: error.response?.data || error.message,
    });
    return {
      success: false,
      status,
      error: error.response?.data || error.message,
      users: [],
    };
  }
};

/**
 * Fetches a list of users for selection purposes (e.g., project owner selection).
 *
 * Uses GET /users/list_for_order/ so Staff still see all lab names after
 * GET /users/ became self-scoped for non-Admins. Labels are "First Last" (no email).
 *
 * @async
 * @param {string} token - The authentication token of the user.
 * @param {function} setUserSelectList - Callback function to set the user select list.
 * @returns {Promise<Object>} - An object with success status, or error messages if the request fails.
 */
export const getUserSelectList = async (token, setUserSelectList) => {
  try {
    const response = await axios.get(`${BACKEND_URL}users/list_for_order/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    /**
     * @param {Object} user
     * @returns {{ value:number, label:string }|null}
     */
    const mapUserToSelectOption = user => {
      const userId = resolveUserId(user);
      if (userId == null) {
        return null;
      }
      const first = String(user.first_name || '').trim();
      const last = String(user.last_name || '').trim();
      const fullName = `${first} ${last}`.trim();
      return {
        value: userId,
        label: fullName || `User ${userId}`,
      };
    };

    const rawUsers = response.data?.results ?? response.data;
    const formattedUsers = (Array.isArray(rawUsers) ? rawUsers : [])
      .map(mapUserToSelectOption)
      .filter(Boolean);

    logInfo('user_client', 'getUserSelectList from list_for_order', {
      count: formattedUsers.length,
    });

    if (setUserSelectList) {
      setUserSelectList(formattedUsers);
    }

    return { success: true, users: formattedUsers };
  } catch (error) {
    logError('user_client', 'getUserSelectList failed', {
      error: error.response?.data || error.message,
    });
    if (setUserSelectList) {
      setUserSelectList([]);
    }
    return { success: false, error: error.response?.data || error.message };
  }
};

/**
 * Fetches a list of users for order creation (ID, first name, last name only).
 *
 * This function sends a GET request to the backend API to retrieve a simplified list of users
 * for use in order creation forms. Returns only essential user information.
 *
 * @async
 * @param {string} token - The authentication token of the user.
 * @returns {Promise<Array|Object>} - An array of user objects with {id, first_name, last_name}, or an error object if the request fails.
 * @throws Will throw an error if the request fails.
 */
export const getUsersForOrder = async (token) => {
  try {
    const response = await axios.get(`${BACKEND_URL}users/list_for_order/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });
    return response.data; // Array of {id, first_name, last_name}
  } catch (error) {
    logError('user_client', 'getUsersForOrder failed', error.response?.data || error.message);
    return { error: error.response?.data || error.message };
  }
};
